import { useReducer } from 'react';
import { type PageFlipMode, RENDER_TYPE, type SCREEN_STATE } from '../types';

// ─── State ───────────────────────────────────────────────────────────────────

export interface PdfUIState {
    scale: number;
    pageNum: number;
    totalPages: number;
    screenState: SCREEN_STATE;
    isShowBar: boolean;
    showOutlines: boolean;
    pageFlipMode: PageFlipMode;
    loading: boolean;
    error: unknown | null;
    renderType: RENDER_TYPE;
    isManualScrolling: boolean;
    isMobileShowSearchDrawer: boolean;
}

export const initialPdfUIState: PdfUIState = {
    scale: 1,
    pageNum: 1,
    totalPages: 0,
    screenState: 'no-full',
    isShowBar: true,
    showOutlines: true,
    pageFlipMode: 'vertical',
    loading: false,
    error: null,
    renderType: RENDER_TYPE.CANVAS,
    isManualScrolling: false,
    isMobileShowSearchDrawer: false,
};

// ─── Actions ─────────────────────────────────────────────────────────────────

export type PdfUIAction =
    | { type: 'SET_SCALE'; payload: number }
    | { type: 'SET_PAGE_NUM'; payload: number }
    | { type: 'SET_TOTAL_PAGES'; payload: number }
    | { type: 'SET_SCREEN_STATE'; payload: SCREEN_STATE }
    | { type: 'SET_IS_SHOW_BAR'; payload: boolean }
    | { type: 'TOGGLE_SHOW_BAR' }
    | { type: 'SET_SHOW_OUTLINES'; payload: boolean }
    | { type: 'TOGGLE_SHOW_OUTLINES' }
    | { type: 'SET_PAGE_FLIP_MODE'; payload: PageFlipMode }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_ERROR'; payload: unknown | null }
    | { type: 'SET_RENDER_TYPE'; payload: RENDER_TYPE }
    | { type: 'SET_IS_MANUAL_SCROLLING'; payload: boolean }
    | { type: 'SET_MOBILE_SEARCH_DRAWER'; payload: boolean }
    /**
     * 批量更新：一次 dispatch 触发一次 re-render（避免多个连续 setState 引起的多次重绘）
     * 使用场景：全屏切换时同时更新 screenState + isShowBar
     */
    | { type: 'BATCH_UPDATE'; payload: Partial<PdfUIState> };

// ─── Reducer ─────────────────────────────────────────────────────────────────

export function pdfUIReducer(state: PdfUIState, action: PdfUIAction): PdfUIState {
    switch (action.type) {
        case 'SET_SCALE':
            return { ...state, scale: action.payload };
        case 'SET_PAGE_NUM':
            return { ...state, pageNum: action.payload };
        case 'SET_TOTAL_PAGES':
            return { ...state, totalPages: action.payload };
        case 'SET_SCREEN_STATE': {
            const screenState = action.payload;
            const isNoFull = screenState === 'no-full';
            // 退出全屏时确保工具栏显示（与原 setScreenState effect 逻辑一致）
            const isShowBar = isNoFull ? true : state.isShowBar;
            return { ...state, screenState, isShowBar };
        }
        case 'SET_IS_SHOW_BAR':
            return { ...state, isShowBar: action.payload };
        case 'TOGGLE_SHOW_BAR':
            // 非全屏模式工具栏始终可见
            return { ...state, isShowBar: state.screenState !== 'full' ? true : !state.isShowBar };
        case 'SET_SHOW_OUTLINES':
            return { ...state, showOutlines: action.payload };
        case 'TOGGLE_SHOW_OUTLINES':
            return { ...state, showOutlines: !state.showOutlines };
        case 'SET_PAGE_FLIP_MODE':
            return { ...state, pageFlipMode: action.payload };
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_ERROR':
            return { ...state, error: action.payload };
        case 'SET_RENDER_TYPE':
            return { ...state, renderType: action.payload };
        case 'SET_IS_MANUAL_SCROLLING':
            return { ...state, isManualScrolling: action.payload };
        case 'SET_MOBILE_SEARCH_DRAWER':
            return { ...state, isMobileShowSearchDrawer: action.payload };
        case 'BATCH_UPDATE':
            return { ...state, ...action.payload };
        default:
            return state;
    }
}

export function usePdfReaderState(overrides?: Partial<PdfUIState>) {
    const [state, dispatch] = useReducer(pdfUIReducer, {
        ...initialPdfUIState,
        ...overrides,
    });
    return { state, dispatch };
}
